import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hierarchy, tree as d3tree } from "d3-hierarchy";
import axios from "axios";
import { Sparkles, ZoomIn, ZoomOut, Maximize2, Loader2 } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = ["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#0ea5e9", "#8b5cf6", "#ef4444", "#14b8a6"];

const linkPath = (s, t) => {
  const mx = (s.x + t.x) / 2;
  return `M${s.x},${s.y} C${mx},${s.y} ${mx},${t.y} ${t.x},${t.y}`;
};

const ROOT_COLOR = "#4f46e5";
const _hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b); let h = 0; const l = (max + min) / 2;
  const d = max - min; const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) { if (max === r) h = ((g - b) / d) % 6; else if (max === g) h = (b - r) / d + 2; else h = (r - g) / d + 4; h *= 60; if (h < 0) h += 360; }
  return [h, s, l];
};
const _hslToHex = (h, s, l) => {
  const c = (1 - Math.abs(2 * l - 1)) * s; const x = c * (1 - Math.abs(((h / 60) % 2) - 1)); const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0]; else if (h < 120) [r, g, b] = [x, c, 0]; else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c]; else if (h < 300) [r, g, b] = [x, 0, c]; else [r, g, b] = [c, 0, x];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
};
// derive a distinct sub-branch color by rotating hue around the parent's base color
const shiftColor = (hex, sibIdx, sibCount) => {
  const [h, s, l] = _hexToHsl(hex);
  const spread = 46; // deg
  const off = sibCount > 1 ? (sibIdx - (sibCount - 1) / 2) * (spread / Math.max(1, sibCount - 1)) * 1.6 : 0;
  const nh = (h + off + 360) % 360;
  const nl = Math.min(0.62, Math.max(0.42, l + (sibIdx % 2 === 0 ? -0.02 : 0.04)));
  return _hslToHex(nh, Math.min(0.85, Math.max(0.5, s)), nl);
};

let _uid = 0;
const tagIds = (n, parentColor) => {
  n.__id = `n${_uid++}`;
  if (n.depth === 0) n.__color = ROOT_COLOR;
  else if (n.depth === 1) n.__color = PALETTE[n.parent.children.indexOf(n) % PALETTE.length];
  else {
    const sibs = n.parent.children || [];
    n.__color = shiftColor(parentColor, sibs.indexOf(n), sibs.length);
  }
  (n.children || []).forEach((c) => tagIds(c, n.__color));
};

export default function MindMapPage() {
  const [keyword, setKeyword] = useState("");
  const [view, setView] = useState("full");
  const [root, setRoot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transform, setTransform] = useState({ x: 60, y: 400, k: 1 });
  const [revealT, setRevealT] = useState(0);

  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const rafRef = useRef(null);

  const generate = useCallback(async (kw, v) => {
    const q = (kw ?? keyword).trim();
    if (q.length < 2) { setError("Type a topic first."); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API_BASE}/mindmap/generate`, { keyword: q, view: v ?? view });
      setRoot(res.data.root);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate. Try again.");
    } finally { setLoading(false); }
  }, [keyword, view]);

  const layout = useMemo(() => {
    if (!root) return null;
    const h = hierarchy(root);
    tagIds(h, ROOT_COLOR);
    d3tree().nodeSize([92, 440]).separation((a, b) => (a.parent === b.parent ? 1 : 1.35))(h);
    const labelW = (n) => {
      const t = String(n.data.title || "");
      const est = 24 + t.length * 9.4 + (n.data.emoji ? 24 : 0);
      return Math.min(300, est);
    };
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    const nodes = h.descendants().map((n) => {
      const sx = n.y, sy = n.x; // horizontal orientation
      const lw = labelW(n);
      minX = Math.min(minX, sx); maxX = Math.max(maxX, sx + lw);
      minY = Math.min(minY, sy); maxY = Math.max(maxY, sy);
      return { id: n.__id, title: n.data.title, emoji: n.data.emoji, depth: n.depth, color: n.__color, sx, sy, lw, hasKids: !!(n.children && n.children.length) };
    });
    const wById = {};
    nodes.forEach((n) => { wById[n.id] = n.lw; });
    const links = h.links().map((l) => ({
      id: l.target.__id, color: l.target.__color,
      s: { x: l.source.y + (wById[l.source.__id] || 0), y: l.source.x }, // from END of parent's underline
      t: { x: l.target.y, y: l.target.x },
    }));
    const appear = {};
    h.descendants().forEach((n) => {
      appear[n.__id] = n.depth * 260 + (n.parent ? n.parent.children.indexOf(n) * 70 : 0);
    });
    const total = Math.max(0, ...Object.values(appear));
    return { nodes, links, bounds: { minX, maxX, minY, maxY }, appear, total };
  }, [root]);

  useEffect(() => {
    if (!layout) return;
    setRevealT(0);
    const start = performance.now();
    const step = (now) => {
      const t = now - start;
      setRevealT(t);
      if (t < layout.total + 600) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [layout]);

  const fit = useCallback(() => {
    if (!layout || !svgRef.current) return;
    const { minX, maxX, minY, maxY } = layout.bounds;
    const rect = svgRef.current.getBoundingClientRect();
    const topPad = 110, botPad = 40;
    const availH = Math.max(200, rect.height - topPad - botPad);
    const k = 0.82; // fixed readable zoom — big fonts; map can overflow & pan
    setTransform({
      k,
      x: 70 - minX * k,
      y: topPad + availH / 2 - ((minY + maxY) / 2) * k,
    });
  }, [layout]);
  useEffect(() => { const t = setTimeout(fit, 60); return () => clearTimeout(t); }, [fit]);

  const zoomBy = (f) => setTransform((v) => ({ ...v, k: Math.min(2, Math.max(0.3, v.k * f)) }));
  const onWheel = (e) => {
    e.preventDefault();
    const rect = svgRef.current ? svgRef.current.getBoundingClientRect() : { left: 0, top: 0 };
    const px = e.clientX - rect.left, py = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.12 : 0.89;
    setTransform((v) => {
      const nk = Math.min(2, Math.max(0.3, v.k * factor));
      const s = nk / v.k;
      return { k: nk, x: px - (px - v.x) * s, y: py - (py - v.y) * s };
    });
  };
  const onDown = (e) => { dragRef.current = { x: e.clientX, y: e.clientY, vx: transform.x, vy: transform.y }; };
  const onMove = (e) => { const d = dragRef.current; if (!d) return; const cx = e.clientX, cy = e.clientY; setTransform((v) => ({ ...v, x: d.vx + (cx - d.x), y: d.vy + (cy - d.y) })); };
  const onUp = () => { dragRef.current = null; };
  const _dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      dragRef.current = null;
      const [a, b] = [e.touches[0], e.touches[1]];
      const rect = svgRef.current ? svgRef.current.getBoundingClientRect() : { left: 0, top: 0 };
      pinchRef.current = { dist: _dist(a, b) || 1, cx: (a.clientX + b.clientX) / 2 - rect.left, cy: (a.clientY + b.clientY) / 2 - rect.top, k: transform.k, x: transform.x, y: transform.y };
    } else if (e.touches.length === 1) {
      pinchRef.current = null;
      const t = e.touches[0];
      dragRef.current = { x: t.clientX, y: t.clientY, vx: transform.x, vy: transform.y };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const p = pinchRef.current;
      const ratio = _dist(e.touches[0], e.touches[1]) / p.dist;
      setTransform(() => {
        const nk = Math.min(2, Math.max(0.3, p.k * ratio));
        const s = nk / p.k;
        return { k: nk, x: p.cx - (p.cx - p.x) * s, y: p.cy - (p.cy - p.y) * s };
      });
      return;
    }
    const d = dragRef.current; if (!d || e.touches.length !== 1) return;
    const t = e.touches[0]; const cx = t.clientX, cy = t.clientY;
    setTransform((v) => ({ ...v, x: d.vx + (cx - d.x), y: d.vy + (cy - d.y) }));
  };
  const onTouchEnd = () => { dragRef.current = null; pinchRef.current = null; };

  const visible = (id) => !layout || revealT >= (layout.appear[id] ?? 0);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-stone-50" data-testid="mindmap-page"
      style={{ backgroundImage: "radial-gradient(#e7e5e4 1px, transparent 1px)", backgroundSize: "22px 22px" }}>
      <div className="absolute top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-b border-stone-200 px-3 py-2.5 shadow-sm">
        <div className="flex items-center gap-3 max-w-5xl mx-auto">
          <div className="hidden md:flex items-center gap-2 text-sm font-bold text-stone-800 shrink-0 font-heading">
            <Sparkles className="w-4 h-4 text-orange-500" /> CLARIAS Mind Map
          </div>
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
            placeholder="Enter topic (e.g., Fundamental Rights, Inflation)…"
            data-testid="mindmap-input"
            className="flex-1 min-w-0 rounded-xl bg-stone-50 focus:bg-white border border-stone-200 hover:border-stone-300 focus:border-orange-500 outline-none focus:ring-2 focus:ring-orange-500/20 px-4 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 transition-all duration-150" />
          <button onClick={() => generate()} disabled={loading}
            data-testid="mindmap-generate-btn"
            className="inline-flex items-center gap-1.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm px-5 py-2.5 shrink-0 border border-orange-600/20 shadow-sm disabled:opacity-50 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/30">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} <span className="hidden sm:inline">Generate</span>
          </button>
        </div>
        {error && <p className="text-xs text-rose-600 mt-1.5 text-center" data-testid="mindmap-error">{error}</p>}
      </div>

      <svg ref={svgRef} className="w-full h-full cursor-grab active:cursor-grabbing"
        style={{ touchAction: "none" }}
        onWheel={onWheel} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
        data-testid="mindmap-svg">
        {layout && (
          <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
            {layout.links.map((l) => {
              const color = l.color;
              return (
                <path key={`l-${l.id}`} d={linkPath(l.s, l.t)} fill="none" stroke={color}
                  strokeWidth={2.4} strokeLinecap="round" pathLength={1} strokeDasharray={1}
                  style={{ strokeDashoffset: visible(l.id) ? 0 : 1, transition: "stroke-dashoffset 500ms ease", opacity: 0.9 }} />
              );
            })}
            {layout.nodes.map((n) => {
              const color = n.color;
              const isRoot = n.depth === 0;
              return (
                <g key={n.id} transform={`translate(${n.sx},${n.sy})`}
                  style={{ opacity: visible(n.id) ? 1 : 0, transition: "opacity 260ms ease" }}
                  data-testid={`mindmap-node-d${n.depth}`}>
                  {/* label sits on a colored underline; branch dot at the underline's right end */}
                  <foreignObject x={0} y={-52} width={320} height={56} style={{ overflow: "visible" }}>
                    <div className="flex items-end h-[54px] pb-1" style={{ borderBottom: `2.5px solid ${color}`, width: n.lw }}>
                      {isRoot && <span className="text-[13px] font-semibold text-slate-400 mr-1 tracking-wider uppercase">in</span>}
                      {n.emoji && <span className="text-[20px] leading-none mr-1.5">{n.emoji}</span>}
                      <span
                        className="leading-snug text-slate-700"
                        style={{ fontSize: isRoot ? 23 : n.depth === 1 ? 20 : n.depth === 2 ? 19 : 18, fontWeight: isRoot ? 700 : n.depth === 1 ? 600 : 400, whiteSpace: "normal", display: "block" }}>
                        {n.title}
                      </span>
                    </div>
                  </foreignObject>
                  {n.hasKids && <circle cx={n.lw} cy={0} r={5} fill="#fff" stroke={color} strokeWidth={2.4} />}
                </g>
              );
            })}
          </g>
        )}
      </svg>

      {!layout && !loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
          <Sparkles className="w-10 h-10 text-orange-300 mb-3" />
          <h2 className="text-xl font-bold text-stone-800 font-heading">Type any topic to grow a mind map</h2>
          <p className="text-sm text-stone-500 mt-1">e.g. Fundamental Rights, Inflation, Monsoon, GST</p>
        </div>
      )}
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-testid="mindmap-loading">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-2" />
          <p className="text-sm text-stone-500">Building your mind map…</p>
        </div>
      )}

      {layout && (
        <div className="absolute bottom-6 right-4 z-20 flex flex-col gap-1 bg-white/90 backdrop-blur-md border border-stone-200 rounded-xl p-1 shadow-md">
          <button onClick={() => zoomBy(1.2)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-stone-100 text-stone-600 hover:text-stone-900 transition-colors duration-150" data-testid="mindmap-zoom-in"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => zoomBy(0.83)} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-stone-100 text-stone-600 hover:text-stone-900 transition-colors duration-150" data-testid="mindmap-zoom-out"><ZoomOut className="w-4 h-4" /></button>
          <button onClick={fit} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-stone-100 text-stone-600 hover:text-stone-900 transition-colors duration-150" data-testid="mindmap-fit"><Maximize2 className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}
